<div class="page-container flex min-h-0 flex-1 flex-col gap-8">
  @if (loadState.loading() && !form()) {
    <section class="px-2 py-16 text-center text-sm text-neutral-500">
      Loading form…
    </section>
  } @else if (form(); as data) {
    @if (isPublished()) {
      <app-page-header
        [title]="data.name"
        description="Your form is ready to share. Copy the link, scan the QR code, or embed it on your site."
        backLabel="Forms"
        backLink="/forms">
        <div class="flex shrink-0 flex-wrap items-center gap-2">
          <lib-signal-button
            appearance="outline"
            fillColor="#ffffff"
            iconSrc="assets/icons/eye-outline.svg"
            [fullWidth]="false"
            [disabled]="!publishedFormUrl()"
            (clicked)="viewForm()">
            View form
          </lib-signal-button>
          <lib-signal-button
            iconSrc="assets/icons/square-edit-outline.svg"
            [fullWidth]="false"
            (clicked)="editForm()">
            Edit form
          </lib-signal-button>
        </div>
      </app-page-header>
    } @else {
      <app-page-header
        title="Form is not published"
        description="Publish this form from the builder to get a share link, QR code, and embed snippet."
        backLabel="Forms"
        backLink="/forms">
        <lib-signal-button [fullWidth]="false" (clicked)="editForm()">
          Open builder
        </lib-signal-button>
      </app-page-header>
    }

    @if (isPublished()) {
      <div class="grid gap-4 xl:grid-cols-3">
        <section class="flex flex-col rounded-2xl bg-white p-5 sm:p-6">
          <div class="flex items-start gap-3">
            <span
              class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-sky-100 text-sky-700"
              aria-hidden="true">
              <app-svg-icon src="assets/icons/link-variant.svg" [size]="20" />
            </span>
            <div class="min-w-0">
              <h3 class="text-base font-semibold text-slate-900">Share link</h3>
              <p class="mt-0.5 text-sm text-slate-500">
                Share this form with anyone using the link.
              </p>
            </div>
          </div>

          <div class="mt-5 flex flex-1 flex-col gap-3">
            @if (hasShortLink()) {
              <div
                class="grid grid-cols-2 gap-2"
                role="radiogroup"
                aria-label="Link type">
                <button
                  type="button"
                  role="radio"
                  class="relative flex cursor-pointer items-center gap-2 rounded-lg border bg-white px-2.5 py-2 text-left transition"
                  [class.border-primary-600]="!useShortLink()"
                  [class.bg-primary-50]="!useShortLink()"
                  [class.border-slate-200]="useShortLink()"
                  [attr.aria-checked]="!useShortLink()"
                  (click)="setLinkType('standard')">
                  @if (!useShortLink()) {
                    <span
                      class="absolute top-1.5 right-1.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-primary-600 text-white"
                      aria-hidden="true">
                      <svg class="h-2 w-2" viewBox="0 0 12 12" fill="none">
                        <path
                          d="M2.5 6.2 4.8 8.5 9.5 3.5"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round" />
                      </svg>
                    </span>
                  }
                  <span
                    class="flex h-7 w-7 shrink-0 items-center justify-center rounded-md"
                    [class.bg-primary-100]="!useShortLink()"
                    [class.text-primary-600]="!useShortLink()"
                    [class.bg-slate-100]="useShortLink()"
                    [class.text-slate-600]="useShortLink()"
                    aria-hidden="true">
                    <app-svg-icon src="assets/icons/link-variant.svg" [size]="14" />
                  </span>
                  <span
                    class="min-w-0 pr-3 text-xs font-semibold"
                    [class.text-primary-700]="!useShortLink()"
                    [class.text-slate-800]="useShortLink()">
                    Standard link
                  </span>
                </button>

                <button
                  type="button"
                  role="radio"
                  class="relative flex cursor-pointer items-center gap-2 rounded-lg border bg-white px-2.5 py-2 text-left transition"
                  [class.border-primary-600]="useShortLink()"
                  [class.bg-primary-50]="useShortLink()"
                  [class.border-slate-200]="!useShortLink()"
                  [attr.aria-checked]="useShortLink()"
                  (click)="setLinkType('short')">
                  @if (useShortLink()) {
                    <span
                      class="absolute top-1.5 right-1.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-primary-600 text-white"
                      aria-hidden="true">
                      <svg class="h-2 w-2" viewBox="0 0 12 12" fill="none">
                        <path
                          d="M2.5 6.2 4.8 8.5 9.5 3.5"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round" />
                      </svg>
                    </span>
                  }
                  <span
                    class="flex h-7 w-7 shrink-0 items-center justify-center rounded-md"
                    [class.bg-primary-100]="useShortLink()"
                    [class.text-primary-600]="useShortLink()"
                    [class.bg-slate-100]="!useShortLink()"
                    [class.text-slate-600]="!useShortLink()"
                    aria-hidden="true">
                    <app-svg-icon src="assets/icons/link-variant.svg" [size]="14" />
                  </span>
                  <span
                    class="min-w-0 pr-3 text-xs font-semibold"
                    [class.text-primary-700]="useShortLink()"
                    [class.text-slate-800]="!useShortLink()">
                    Short link
                  </span>
                </button>
              </div>
            }

            <input
              type="text"
              readonly
              class="min-w-0 w-full rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 font-mono text-xs text-slate-800 outline-none"
              [value]="activeShareUrl()"
              aria-label="Share URL" />
            <lib-signal-button
              iconSrc="assets/icons/content-copy.svg"
              [fullWidth]="true"
              (clicked)="copyShare('link')">
              {{ shareCopied() === 'link' ? 'Copied' : 'Copy link' }}
            </lib-signal-button>
          </div>
        </section>

        <section class="flex flex-col rounded-2xl bg-white p-5 sm:p-6">
          <div class="flex items-start gap-3">
            <span
              class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700"
              aria-hidden="true">
              <app-svg-icon src="assets/icons/qrcode.svg" [size]="20" />
            </span>
            <div class="min-w-0">
              <h3 class="text-base font-semibold text-slate-900">QR code</h3>
              <p class="mt-0.5 text-sm text-slate-500">Scan to open this form.</p>
            </div>
          </div>

          <div class="mt-5 flex flex-1 flex-col items-center justify-between gap-4">
            @if (qrDataUrl()) {
              <img
                [src]="qrDataUrl()"
                alt="QR code for form share link"
                class="h-40 w-40 rounded-xl border border-slate-100 bg-white p-2" />
            } @else {
              <div
                class="flex h-40 w-40 items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50 text-xs text-slate-400">
                Generating…
              </div>
            }
            <lib-signal-button
              appearance="outline"
              iconSrc="assets/icons/download-outline.svg"
              [fullWidth]="true"
              [disabled]="!qrDataUrl()"
              (clicked)="downloadQr()">
              Download QR code
            </lib-signal-button>
          </div>
        </section>

        <section class="flex flex-col rounded-2xl bg-white p-5 sm:p-6">
          <div class="flex items-start gap-3">
            <span
              class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-orange-100 text-orange-700"
              aria-hidden="true">
              <app-svg-icon src="assets/icons/xml.svg" [size]="20" />
            </span>
            <div class="min-w-0">
              <h3 class="text-base font-semibold text-slate-900">Embed on your website</h3>
              <p class="mt-0.5 text-sm text-slate-500">
                Copy and paste this code into your website.
              </p>
            </div>
          </div>

          <div class="mt-4 flex shrink-0 rounded-xl bg-slate-100 p-1">
            <button
              type="button"
              class="flex-1 cursor-pointer rounded-lg px-2 py-1.5 text-xs font-semibold transition"
              [class.bg-white]="embedTab() === 'iframe'"
              [class.text-violet-700]="embedTab() === 'iframe'"
              [class.shadow-sm]="embedTab() === 'iframe'"
              [class.text-slate-500]="embedTab() !== 'iframe'"
              (click)="setEmbedTab('iframe')">
              Iframe
            </button>
            <button
              type="button"
              class="flex-1 cursor-pointer rounded-lg px-2 py-1.5 text-xs font-semibold transition"
              [class.bg-white]="embedTab() === 'script'"
              [class.text-violet-700]="embedTab() === 'script'"
              [class.shadow-sm]="embedTab() === 'script'"
              [class.text-slate-500]="embedTab() !== 'script'"
              (click)="setEmbedTab('script')">
              Script
            </button>
            <button
              type="button"
              class="flex-1 cursor-pointer rounded-lg px-2 py-1.5 text-xs font-semibold transition"
              [class.bg-white]="embedTab() === 'webcomponent'"
              [class.text-violet-700]="embedTab() === 'webcomponent'"
              [class.shadow-sm]="embedTab() === 'webcomponent'"
              [class.text-slate-500]="embedTab() !== 'webcomponent'"
              (click)="setEmbedTab('webcomponent')">
              Web
            </button>
          </div>

          <div class="mt-4 flex flex-1 flex-col gap-3">
            <pre
              class="min-h-0 flex-1 overflow-auto whitespace-pre rounded-xl border border-slate-100 bg-slate-50 px-3 py-3 font-mono text-[11px] leading-5 text-slate-800">{{ embedSnippet() }}</pre>
            <lib-signal-button
              appearance="outline"
              iconSrc="assets/icons/content-copy.svg"
              [fullWidth]="true"
              (clicked)="copyShare('embed')">
              {{ shareCopied() === 'embed' ? 'Copied' : 'Copy code' }}
            </lib-signal-button>
          </div>
        </section>
      </div>

      <section class="overflow-hidden rounded-2xl bg-white">
        <div
          class="flex flex-col gap-4 border-b border-slate-100 px-5 py-5 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <div class="flex items-center gap-3.5">
            <span
              class="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-sky-50 text-sky-700"
              aria-hidden="true">
              <app-svg-icon src="assets/icons/cog-outline.svg" [size]="22" />
            </span>
            <div>
              <h3 class="text-base font-semibold tracking-tight text-slate-950">
                Publish summary
              </h3>
              <p class="mt-0.5 text-sm text-slate-500">
                Current sharing configuration for this form.
              </p>
            </div>
          </div>

          <lib-signal-button
            class="shrink-0 self-start sm:self-auto"
            iconSrc="assets/icons/cog-outline.svg"
            [fullWidth]="false"
            (clicked)="editSettings()">
            Edit settings
          </lib-signal-button>
        </div>

        @if (publishSettings(); as settings) {
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
            <div class="border-b border-slate-100 px-5 py-6 text-center sm:border-r lg:border-b-0 sm:px-6">
              <div
                class="mx-auto flex h-11 w-11 items-center justify-center rounded-xl bg-slate-50 text-sky-600">
                <app-svg-icon src="assets/icons/eye-circle-outline.svg" [size]="24" />
              </div>
              <p class="mt-3.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                Visibility
              </p>
              <p class="mt-1 text-sm font-semibold tracking-tight text-slate-950">
                {{ visibilityLabel(settings.visibility) }}
              </p>
              <p class="mt-1.5 text-xs text-slate-500">
                {{ visibilityHint(settings.visibility) }}
              </p>
            </div>

            <div class="border-b border-slate-100 px-5 py-6 text-center lg:border-b-0 lg:border-r sm:px-6">
              @if (settings.publishTiming === 'scheduled') {
                <img
                  src="assets/illustrations/form-scheduled.svg"
                  alt=""
                  class="mx-auto h-auto w-full max-w-[160px] object-contain"
                  width="520"
                  height="340" />
              } @else {
                <div
                  class="mx-auto flex h-11 w-11 items-center justify-center rounded-xl bg-slate-50 text-amber-600">
                  <app-svg-icon src="assets/icons/lightning-bolt-outline.svg" [size]="24" />
                </div>
              }
              <p class="mt-3.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                {{
                  settings.publishTiming === 'scheduled'
                    ? 'Scheduled on'
                    : 'Published on'
                }}
              </p>
              <p class="mt-1 text-sm font-semibold tracking-tight text-slate-950">
                {{ formatDateTime(availableFromDate(settings)) }}
              </p>
              @if (settings.closeOnScheduleEnd && settings.scheduledEndAt) {
                <p class="mt-1.5 inline-flex items-center justify-center gap-1.5 text-xs text-slate-500">
                  <app-svg-icon
                    class="shrink-0 text-slate-400"
                    src="assets/icons/clock-outline.svg"
                    [size]="16" />
                  Closes {{ formatDateTime(settings.scheduledEndAt) }}
                </p>
              }
            </div>

            <div class="border-b border-slate-100 px-5 py-6 text-center sm:border-b-0 sm:border-r sm:px-6">
              <div
                class="mx-auto flex h-11 w-11 items-center justify-center rounded-xl bg-slate-50 text-violet-600">
                <app-svg-icon src="assets/icons/database-outline.svg" [size]="24" />
              </div>
              <p class="mt-3.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                Max submissions
              </p>
              <p class="mt-1 text-sm font-semibold tracking-tight text-slate-950">
                {{ maxSubmissionsLabel(settings.maxSubmissions) }}
              </p>
              <p class="mt-1.5 text-xs text-slate-500">
                {{
                  settings.formSubmissionDraftRetentionHours === 0
                    ? 'Drafts: off'
                    : 'Draft retention: ' +
                      draftRetentionLabel(settings.formSubmissionDraftRetentionHours)
                }}
              </p>
            </div>

            <div class="px-5 py-6 text-center sm:px-6">
              <div
                class="mx-auto flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
                <app-svg-icon src="assets/icons/check-circle-outline.svg" [size]="24" />
              </div>
              <p class="mt-3.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                Approval
              </p>
              <p class="mt-1 text-sm font-semibold tracking-tight text-slate-950">
                {{ settings.requireSubmissionApproval ? 'Required' : 'Not required' }}
              </p>
              <p class="mt-1.5 text-xs text-slate-500">
                {{ approvalHint(settings.requireSubmissionApproval) }}
              </p>
            </div>
          </div>
        } @else {
          <p class="px-5 py-6 text-sm text-slate-500 sm:px-6">
            No publish settings found for this form.
          </p>
        }
      </section>
    }
  } @else {
    <app-page-header
      title="Form not found"
      description="We could not load this form. It may have been deleted or the link is invalid."
      backLabel="Forms"
      backLink="/forms" />
  }
</div>
